'use client';

import { RestApiDeliveryMethodAreaDto } from '@order-mage/integration-sdk';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, CSSProperties, Fragment, useEffect, useRef } from 'react';
import { getPriceValueTranslation } from '@/entities/cart';
import { useDeliveryZonesDisplay } from '@/entities/venue/lib/useDeliveryZonesDisplay';
import { isTimePlanActiveNow } from '@/entities/venue/lib/useTimeAvailability';
import { VenueAvailabilityKind } from '@/entities/venue/lib/venue-availability.utils';
import { useVenueAvailabilityStore } from '@/entities/venue/model/venue-availability.store';
import { cn } from '@/shared/lib/cn';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { Badge } from '@/shared/ui/badge';
import { Icon } from '@/shared/ui/icon';

// Helper function to get price value using the same logic as useGetItemPrice
const getPriceValue = (
    priceValues:
        | Array<{
              currency?: string;
              country?: Country | string | null;
              countryCode?: Country | string | null;
              amount: string;
          }>
        | undefined,
    selectedCountry: Country,
    selectedCurrency: Currency,
): { amount: string; currency: string } | null => {
    if (!priceValues || !Array.isArray(priceValues)) return null;

    // Najít priceValue s konkrétní zemí a měnou (vyšší priorita)
    let priceValue = priceValues.find(
        (pv) =>
            pv.currency === selectedCurrency &&
            (pv.country === selectedCountry || pv.countryCode === selectedCountry),
    );

    // Pokud nenajdeme priceValue s konkrétní zemí, použijeme fallback (bez země)
    if (!priceValue) {
        priceValue = priceValues.find(
            (pv) => pv.currency === selectedCurrency && !pv.country && !pv.countryCode,
        );
    }

    if (!priceValue) {
        return null;
    }

    return {
        amount: priceValue.amount,
        currency: priceValue.currency || selectedCurrency,
    };
};

type ZoneProps = {
    area: RestApiDeliveryMethodAreaDto;
    zoneKey: string;
    isSelected: boolean;
    canSelectZones: boolean;
    onToggleZone?: (zoneKey: string) => void;
};

const Zone = ({ area, zoneKey, isSelected, canSelectZones, onToggleZone }: ZoneProps) => {
    const t = useTranslations();
    const [selectedCurrency] = useSelectedCurrency();
    const getLocalizedValue = useGetLocalizedValue();
    const isVenueOpen = useVenueAvailabilityStore((s) => s.status.kind === VenueAvailabilityKind.Open);
    const selectedCountry = Country.CZ;
    const zoneRef = useRef<HTMLButtonElement>(null);

    // Get delivery fee from area.taxedPrice.priceGroup.priceValues
    const areaPriceValue = getPriceValue(
        area.taxedPrice?.priceGroup?.priceValues,
        selectedCountry,
        selectedCurrency,
    );

    // Delivery fee is only from area, not from delivery method
    const deliveryFee = areaPriceValue ? parseFloat(areaPriceValue.amount) : null;

    // Get min order value from area.priceRules with type "MIN_RESTRICTION"
    const minRestrictionRule = area.priceRules?.find((rule) => rule.type === 'MIN_RESTRICTION');
    const minOrderValuePrice = minRestrictionRule
        ? getPriceValue(minRestrictionRule.priceGroup?.priceValues, selectedCountry, selectedCurrency)
        : null;

    // Get free from (discount threshold) from area.priceRules with type "DISCOUNT_THRESHOLD"
    const discountThresholdRule = area.priceRules?.find((rule) => rule.type === 'DISCOUNT_THRESHOLD');
    const discountPercent = discountThresholdRule?.params?.discount ?? null;
    const isFreeDelivery = discountPercent === '100';
    const freeFromPrice = discountThresholdRule
        ? getPriceValue(discountThresholdRule.priceGroup?.priceValues, selectedCountry, selectedCurrency)
        : null;

    // TODO: Data does not contain delivery area timezones
    const deliveryUntil = null;

    // Gated on the venue being open for the same reason the cart badge is: while the
    // restaurant is closed the closure is the reason, and a per-zone time note next to it
    // would contradict what the cart says on the very same screen.
    const isTimeBlockedNow =
        isVenueOpen &&
        (area.restrictAvailableTime?.length ?? 0) > 0 &&
        !isTimePlanActiveNow(area.restrictAvailableTime ?? []);

    const zoneColor = area.color ? `#${area.color}` : '#3b82f6';

    useEffect(() => {
        if (isSelected) {
            zoneRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [isSelected]);

    return (
        <button
            ref={zoneRef}
            type="button"
            className={cn(
                'delivery-zones-modal__legend-zone',
                canSelectZones && 'delivery-zones-modal__legend-zone--selectable',
                isSelected && 'delivery-zones-modal__legend-zone--selected',
            )}
            onClick={canSelectZones ? () => onToggleZone?.(zoneKey) : undefined}
            disabled={!canSelectZones}
            aria-pressed={isSelected}
            data-testid="delivery-zones-modal-zone"
            style={{ '--zone-color': zoneColor } as CSSProperties}
        >
            <span
                className="delivery-zones-modal__legend-zone-name"
                data-testid="delivery-zones-modal-zone-name"
            >
                {getLocalizedValue(area.name)}
            </span>
            <div className="delivery-zones-modal__legend-badges">
                {area.deliveryAvailable === false && (
                    <Badge variant="error" data-testid="delivery-zones-modal-undeliverable-badge">
                        {t('DELIVERY_ZONES_MODAL.UNDELIVERABLE')}
                    </Badge>
                )}
                {area.deliveryAvailable !== false && isTimeBlockedNow && (
                    <Badge variant="error" data-testid="delivery-zones-modal-time-restricted-badge">
                        {t('CART_DELIVERY_BOX.AREA_TIME_RESTRICTED')}
                    </Badge>
                )}
                {deliveryFee !== null && area.deliveryAvailable !== false && (
                    <Badge
                        variant="customColor"
                        appearance="outline"
                        data-testid="delivery-zones-modal-delivery-fee-badge"
                    >
                        <Icon name="coins" width={14} height={14} />
                        {t('DELIVERY_ZONES_MODAL.DELIVERY_FEE', {
                            price: getPriceValueTranslation({
                                amount: deliveryFee,
                                currency: selectedCurrency,
                                t,
                            }),
                        })}
                    </Badge>
                )}
                {minOrderValuePrice && (
                    <Badge
                        variant="customColor"
                        appearance="outline"
                        data-testid="delivery-zones-modal-min-order-badge"
                    >
                        {t('DELIVERY_ZONES_MODAL.MIN_ORDER_VALUE', {
                            price: getPriceValueTranslation({
                                amount: parseFloat(minOrderValuePrice.amount),
                                currency: minOrderValuePrice.currency as Currency,
                                t,
                            }),
                        })}
                    </Badge>
                )}
                {freeFromPrice && (
                    <Badge
                        variant="customColor"
                        appearance="outline"
                        data-testid="delivery-zones-modal-free-from-badge"
                    >
                        {isFreeDelivery
                            ? t('DELIVERY_ZONES_MODAL.FREE_FROM', {
                                  price: getPriceValueTranslation({
                                      amount: parseFloat(freeFromPrice.amount),
                                      currency: freeFromPrice.currency as Currency,
                                      t,
                                  }),
                              })
                            : t('DELIVERY_ZONES_MODAL.DISCOUNT_FROM', {
                                  percentage: discountPercent ?? '',
                                  price: getPriceValueTranslation({
                                      amount: parseFloat(freeFromPrice.amount),
                                      currency: freeFromPrice.currency as Currency,
                                      t,
                                  }),
                              })}
                    </Badge>
                )}
                {deliveryUntil && (
                    <Badge
                        variant="customColor"
                        appearance="outline"
                        data-testid="delivery-zones-modal-delivery-until-badge"
                    >
                        <Icon name="clock" width={14} height={14} />
                        {t('DELIVERY_ZONES_MODAL.DELIVERY_UNTIL', { time: deliveryUntil })}
                    </Badge>
                )}
            </div>
        </button>
    );
};

type Props = ComponentPropsWithoutRef<'div'>;
export const DeliveryZonesModalLegend = ({ className, ...props }: Props) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const {
        allZones,
        userZone,
        visibleZones,
        shouldPersonalize,
        showAllZones,
        setShowAllZones,
        selectedZoneKey,
        toggleSelectedZone,
        canSelectZones,
    } = useDeliveryZonesDisplay();

    const legendTitle =
        shouldPersonalize && userZone
            ? t('DELIVERY_ZONES_MODAL.YOUR_ZONE_NAMED', {
                  name: getLocalizedValue(userZone.area.name),
              })
            : t('DELIVERY_ZONES_MODAL.ZONE_DESCRIPTIONS');

    const canToggleZones = shouldPersonalize && allZones.length > 1;

    return (
        <div className={cn('delivery-zones-modal__legend', className)} {...props}>
            <h3
                className="delivery-zones-modal__legend-title"
                data-testid="delivery-zones-modal-legend-title"
            >
                {legendTitle}
            </h3>
            {visibleZones.map(({ area, deliveryMethod }, idx) => (
                <Fragment key={`${deliveryMethod.id}-${area.id}`}>
                    {idx > 0 && <hr className="delivery-zones-modal__legend-divider" />}
                    <Zone
                        area={area}
                        zoneKey={`${deliveryMethod.id}-${area.id}`}
                        isSelected={selectedZoneKey === `${deliveryMethod.id}-${area.id}`}
                        canSelectZones={canSelectZones}
                        onToggleZone={toggleSelectedZone}
                    />
                </Fragment>
            ))}
            {canToggleZones && setShowAllZones && (
                <>
                    <hr className="delivery-zones-modal__legend-divider" />
                    <button
                        type="button"
                        className="delivery-zones-modal__legend-show-more"
                        onClick={() => setShowAllZones(!showAllZones)}
                        data-testid="delivery-zones-modal-show-more-button"
                    >
                        {showAllZones
                            ? t('DELIVERY_ZONES_MODAL.HIDE_OTHER_ZONES')
                            : t('DELIVERY_ZONES_MODAL.SHOW_MORE_ZONES')}
                    </button>
                </>
            )}
        </div>
    );
};
